import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import {
  ArrowRight, ShieldCheck, GitBranch, Activity, Award, BookOpen, FileSearch,
  Building2, Factory, Fuel, Truck, Cog, Warehouse, ChevronRight, Check, MoveRight
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1629447388369-760612337eff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxvaWwlMjByZWZpbmVyeSUyMG5pZ2h0JTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3ODAwMTMxMjN8MA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.pexels.com/photos/372796/pexels-photo-372796.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SERVICES_BG = "https://images.unsplash.com/photo-1727461553668-45322401f863?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHw0fHxvaWwlMjBhbmQlMjBnYXMlMjB3b3JrZXIlMjBvZmZzaG9yZSUyMGRhcmt8ZW58MHx8fHwxNzgwMDEzMTIzfDA&ixlib=rb-4.1.0&q=85";

const REGS = [
  "RES/811", "RES/776", "DACG CRE", "Lineamientos CNH",
  "Anexo 30 RMF", "Anexo 31 RMF", "API MPMS", "AGA Report", "SAT", "NOM-005"
];

const SERVICES = [
  { icon: ShieldCheck, code: "01", title: "Conformación del SGM", desc: "Diseñamos e implementamos el Sistema de Gestión de las Mediciones que cumple los requisitos técnicos y normativos de sus sistemas de medición." },
  { icon: BookOpen, code: "02", title: "Capacitación Especializada", desc: "Sesiones impartidas por expertos en metrología y normatividad para desarrollar a su personal técnico y operativo." },
  { icon: Activity, code: "03", title: "Calibración y Verificación", desc: "Actividades periódicas de calibración y verificación de sus sistemas de medición conforme a las especificaciones de las autoridades." },
  { icon: FileSearch, code: "04", title: "Auditorías de Cumplimiento", desc: "Evaluación integral del cumplimiento de su Sistema de Gestión de las Mediciones por terceros acreditados." },
  { icon: GitBranch, code: "05", title: "Estimación de Incertidumbre", desc: "Cálculo formal de la incertidumbre asociada a sus sistemas de medición conforme a la guía GUM y normatividad vigente." },
  { icon: Award, code: "06", title: "Asistencia en Inspecciones", desc: "Preparación de evidencias y acompañamiento técnico ante inspecciones de CRE, CNH, SAT y terceros acreditados." },
];

const SECTORS = [
  { icon: Fuel, name: "Operadores Petroleros" },
  { icon: Factory, name: "Refinerías" },
  { icon: Cog, name: "Estaciones de Proceso" },
  { icon: GitBranch, name: "Transporte por Ducto" },
  { icon: Truck, name: "Transporte por Autotanque" },
  { icon: Warehouse, name: "Terminales de Almacenamiento" },
  { icon: Building2, name: "Estaciones de Servicio" },
  { icon: Activity, name: "Distribución de Gas Natural" },
];

const ADVANTAGES = [
  { num: "01", title: "Amortiza Gastos", desc: "Distribuye los costos operativos del cumplimiento normativo en pagos periódicos predecibles." },
  { num: "02", title: "Desarrolla Personal", desc: "Capacitación continua que fortalece las capacidades técnicas de su equipo interno." },
  { num: "03", title: "Foco en Operación", desc: "Le permite concentrarse en su negocio mientras nosotros gestionamos su cumplimiento técnico." },
  { num: "04", title: "Menos Proveedores", desc: "Una sola póliza integral reduce la carga administrativa de gestionar múltiples vendedores." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "Conformación del SGM", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Gracias por contactarnos. Le responderemos pronto.");
      setForm({ name: "", email: "", phone: "", company: "", service: "Conformación del SGM", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al enviar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#1A1A1F] text-white min-h-screen" data-testid="landing-page">
      <Header />

      {/* HERO */}
      <section id="inicio" className="theme-fixed-dark relative min-h-[100vh] flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1F]/70 via-[#1A1A1F]/80 to-[#1A1A1F]" />
          <div className="absolute inset-0 grid-bg opacity-30" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full grid grid-cols-12 gap-6 py-20">
          <div className="col-span-12 lg:col-span-9">
            <div className="flex items-center gap-3 mb-8" data-testid="hero-overline">
              <span className="w-12 h-px bg-[#F59E0B]" />
              <span className="overline">Sector Hidrocarburos · México</span>
            </div>
            <h1 className="font-heading font-black uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-7xl">
              Implementa un Sistema <br/>
              <span className="text-[#A1A1AA]">de Gestión</span> <span className="text-[#F59E0B]">de las Mediciones</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg lg:text-xl text-[#D4D4D8] leading-relaxed">
              Cumplimiento técnico normativo ante <span className="text-white font-semibold">CRE, CNH y SAT</span>.
              Aseguramos que sus equipos y procesos de medición sean adecuados para su uso previsto y resistan auditorías de terceros acreditados.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <a href="#contacto" className="btn-primary" data-testid="hero-cta-cotizar">
                Cotizar Servicio <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#sgm" className="btn-ghost" data-testid="hero-cta-conocer">
                Conocer Más
              </a>
            </div>

            {/* KPI strip */}
            <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2E2E33] border border-[#2E2E33]">
              {[
                { v: "100%", l: "Cumplimiento" },
                { v: "RES/811", l: "Almacenamiento" },
                { v: "RES/776", l: "Transporte" },
                { v: "24/7", l: "Soporte Técnico" },
              ].map((k) => (
                <div key={k.l} className="bg-[#1A1A1F] px-6 py-6">
                  <div className="font-heading font-black text-3xl text-[#F59E0B]">{k.v}</div>
                  <div className="text-xs font-mono uppercase tracking-widest text-[#A1A1AA] mt-2">{k.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* corner ticks */}
        <div className="absolute top-24 right-10 hidden md:block">
          <div className="font-mono text-[10px] text-[#71717A] uppercase tracking-widest">[ COORDS · 19.43°N / 99.13°W ]</div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="theme-fixed-dark border-y border-[#2E2E33] bg-[#1F1F25] py-6 overflow-hidden">
        <div className="flex gap-16 animate-marquee whitespace-nowrap">
          {[...REGS, ...REGS, ...REGS].map((r, i) => (
            <span key={i} className="font-heading font-bold uppercase tracking-widest text-xl text-[#71717A] flex items-center gap-16">
              {r} <span className="text-[#F59E0B]">●</span>
            </span>
          ))}
        </div>
      </section>

      {/* ABOUT SGM */}
      <section id="sgm" className="py-24 lg:py-32 noise-overlay">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-px bg-[#F59E0B]" />
              <span className="overline">El Sistema</span>
            </div>
            <h2 className="font-heading font-black uppercase tracking-tighter text-4xl lg:text-6xl leading-[0.95] mb-8">
              ¿Qué es el<br/><span className="text-[#F59E0B]">SGM</span>?
            </h2>
            <div className="space-y-6 text-[#D4D4D8] text-base lg:text-lg leading-relaxed">
              <p>
                El <strong className="text-white">Sistema de Gestión de las Mediciones (SGM)</strong> es un sistema de gestión orientado a asegurar que los equipos y procesos de medición son adecuados para su uso previsto, así como gestionar el riesgo de que los equipos de medición produzcan resultados incorrectos.
              </p>
              <p>
                En México, mediante diversas normas y ordenamientos emitidos por <strong className="text-white">CRE</strong>, <strong className="text-white">CNH</strong> y <strong className="text-white">SAT</strong>, se ha establecido la obligación de implementar un SGM por parte de los permisionarios del sector hidrocarburos.
              </p>
              <p>
                Los permisionarios tienen la obligación de reportar a las autoridades los registros de volumen de sus operaciones de recibo, descarga y control de existencias.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-px bg-[#2E2E33] border border-[#2E2E33]">
              {[
                "DACG CRE (RES/811 y RES/776)",
                "Lineamientos Técnicos CNH",
                "Anexos 30 y 31 RMF (SAT)",
                "API MPMS",
                "AGA Report",
                "Estándares Internacionales",
              ].map((r) => (
                <div key={r} className="bg-[#1A1A1F] px-5 py-4 flex items-center gap-3">
                  <Check className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
                  <span className="font-mono text-xs uppercase tracking-wider text-[#D4D4D8]">{r}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#F59E0B]" />
              <img src={ABOUT_IMG} alt="Instrumentación de medición" className="relative w-full h-[500px] object-cover grayscale-[15%]" data-testid="about-image" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1A1A1F] via-[#1A1A1F]/60 to-transparent h-1/2" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-mono text-xs uppercase tracking-widest text-[#F59E0B] mb-2">// Instrumentación</div>
                <div className="font-heading font-bold text-white text-xl uppercase">Precisión · Trazabilidad · Cumplimiento</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARCO NORMATIVO - Anexo 21 / NMX */}
      <section id="marco" className="py-24 lg:py-32 border-t border-[#2E2E33] bg-[#1F1F25] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-12 h-px bg-[#F59E0B]" />
                <span className="overline">Marco Normativo</span>
              </div>
              <h2 className="font-heading font-black uppercase tracking-tighter text-3xl lg:text-5xl leading-[0.95] mb-6">
                La base de sus <span className="text-[#F59E0B]">Controles Volumétricos</span>
              </h2>
              <p className="text-[#D4D4D8] text-base lg:text-lg leading-relaxed">
                El Sistema de Gestión de las Mediciones (SGM) es la base sobre la cual se administrarán todos los <strong className="text-white">Requisitos Metrológicos</strong>, <strong className="text-white">Requisitos de la Regulación</strong> en materia de medición y todas las actividades que influyen en el resultado de tus mediciones.
              </p>
              <p className="mt-5 text-[#D4D4D8] text-base lg:text-lg leading-relaxed">
                Es una <span className="text-[#F59E0B] font-semibold">obligación</span> para todas aquellas personas que deban cumplir con los Controles Volumétricos, especificada en el <strong className="text-white">numeral 21.5.1 del Anexo 21</strong>.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="border border-[#2E2E33] bg-[#1A1A1F]" data-testid="anexo21-block">
                <div className="px-6 lg:px-8 py-5 border-b border-[#2E2E33] flex items-center justify-between flex-wrap gap-2">
                  <div className="font-mono text-xs uppercase tracking-widest text-[#A1A1AA]">
                    // ANEXO 21 — Resolución Miscelánea Fiscal
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#F59E0B]">
                    SAT · CRE · CNH
                  </div>
                </div>
                <div className="p-6 lg:p-8 space-y-5">
                  <p className="text-[#A1A1AA] text-sm italic leading-relaxed">
                    "Especificaciones técnicas de funcionalidad y seguridad de los equipos y programas informáticos para llevar controles volumétricos de hidrocarburos y petrolíferos"
                  </p>

                  <div className="border-l-2 border-[#F59E0B] pl-5 py-1">
                    <div className="font-heading font-bold text-white text-lg uppercase">21.5.1</div>
                    <div className="font-mono text-xs text-[#A1A1AA] uppercase tracking-widest mt-1">
                      Requerimientos de los sistemas de medición
                    </div>
                  </div>

                  <p className="text-[#D4D4D8] text-sm leading-relaxed">
                    Los sistemas de medición deben cumplir con los siguientes requerimientos:
                  </p>

                  <div>
                    <div className="font-heading font-bold text-white text-sm uppercase mb-3">
                      I. Requerimientos generales
                    </div>
                    <div className="flex gap-4">
                      <div className="font-mono text-[#F59E0B] font-bold text-sm">b)</div>
                      <p className="text-[#D4D4D8] text-sm leading-relaxed">
                        Tener implementado un SGM, en cumplimiento de la <strong className="text-white">NMX-CC-10012-IMNC</strong> "Sistemas de Gestión de las Mediciones — Requisitos para los Procesos de Medición y los Equipos de Medición", cuya declaratoria de vigencia por parte de la Secretaría de Economía fue publicada en el DOF el <strong className="text-white">27 de julio de 2004</strong>, o aquélla que la sustituya, para lograr la <span className="text-[#F59E0B]">Confirmación metrológica</span> y el <span className="text-[#F59E0B]">control continuo</span> de los procesos de medición, con la finalidad de que los resultados obtenidos sean conformes a las Especificaciones metrológicas.
                      </p>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-[#2E2E33]">
                    <div className="font-mono text-xs uppercase tracking-widest text-[#A1A1AA] mb-4">
                      // El SGM debe interrelacionar:
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#2E2E33] border border-[#2E2E33]">
                      {[
                        { n: "01", t: "Normatividad y Procedimientos" },
                        { n: "02", t: "Sistemas de Medición" },
                        { n: "03", t: "Responsabilidad y Competencia del Personal" },
                      ].map((p) => (
                        <div key={p.n} className="bg-[#1A1A1F] p-5" data-testid={`sgm-aspect-${p.n}`}>
                          <div className="font-heading font-black text-2xl text-[#F59E0B]/40 mb-2">{p.n}</div>
                          <div className="font-heading font-bold uppercase text-white text-xs leading-tight">{p.t}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="servicios" className="relative py-24 lg:py-32 border-t border-[#2E2E33]">
        <div className="absolute inset-0 opacity-[0.07]">
          <img src={SERVICES_BG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-12 h-px bg-[#F59E0B]" />
                <span className="overline">Servicios</span>
              </div>
              <h2 className="font-heading font-black uppercase tracking-tighter text-4xl lg:text-6xl leading-[0.95]">
                Conformación e<br/>Implementación <span className="text-[#F59E0B]">Integral</span>
              </h2>
            </div>
            <div className="lg:col-span-5 flex items-end">
              <p className="text-[#A1A1AA] text-lg leading-relaxed">
                Nuestra póliza incluye capacitación, calibración, auditorías, estimación de incertidumbre y asistencia técnica ante inspecciones de autoridades y terceros acreditados.
              </p>
            </div>
          </div>

          {/* technical grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2E2E33] border border-[#2E2E33]">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.code}
                  className="group bg-[#1A1A1F] p-10 transition-all duration-300 hover:bg-[#26262C] relative"
                  data-testid={`service-card-${s.code}`}
                >
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-14 h-14 border border-[#2E2E33] group-hover:border-[#F59E0B] flex items-center justify-center transition-colors">
                      <Icon className="w-6 h-6 text-[#F59E0B]" strokeWidth={1.8} />
                    </div>
                    <span className="font-mono text-xs text-[#71717A]">// {s.code}</span>
                  </div>
                  <h3 className="font-heading font-bold uppercase text-xl mb-4 leading-tight">{s.title}</h3>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed">{s.desc}</p>
                  <div className="mt-8 flex items-center gap-2 text-[#F59E0B] opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="font-mono text-xs uppercase tracking-widest">Saber más</span>
                    <MoveRight className="w-4 h-4" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section id="sectores" className="py-24 lg:py-32 border-t border-[#2E2E33] bg-[#1F1F25]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-[#F59E0B]" />
            <span className="overline">Sujetos Obligados</span>
          </div>
          <h2 className="font-heading font-black uppercase tracking-tighter text-4xl lg:text-6xl leading-[0.95] mb-4">
            Sectores que <span className="text-[#F59E0B]">Atendemos</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mb-16">
            Permisionarios del sector hidrocarburos obligados a implementar un Sistema de Gestión de las Mediciones.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2E2E33] border border-[#2E2E33]">
            {SECTORS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.name}
                  className="bg-[#1A1A1F] p-8 group hover:bg-[#26262C] transition-colors flex flex-col items-start gap-4 min-h-[180px]"
                  data-testid={`sector-${i}`}
                >
                  <div className="w-12 h-12 border border-[#2E2E33] group-hover:border-[#F59E0B] flex items-center justify-center transition-colors">
                    <Icon className="w-5 h-5 text-[#F59E0B]" strokeWidth={1.8} />
                  </div>
                  <span className="font-heading font-bold uppercase text-base leading-tight">{s.name}</span>
                  <span className="font-mono text-[10px] text-[#71717A] mt-auto">[ 0{i + 1} ]</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="py-24 lg:py-32 border-t border-[#2E2E33]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-px bg-[#F59E0B]" />
              <span className="overline">Esquema de Póliza</span>
            </div>
            <h2 className="font-heading font-black uppercase tracking-tighter text-4xl lg:text-5xl leading-[0.95] mb-8">
              Ventajas de <span className="text-[#F59E0B]">Trabajar</span> con Nosotros
            </h2>
            <p className="text-[#A1A1AA] text-base leading-relaxed">
              El esquema de póliza brinda a nuestros clientes la tranquilidad de asegurarse que el cumplimiento a las obligaciones en materia de medición y controles volumétricos está siendo llevado a cabo de manera periódica y sistematizada por expertos, sin impactar su operación.
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#2E2E33] border border-[#2E2E33]">
            {ADVANTAGES.map((a) => (
              <div key={a.num} className="bg-[#1A1A1F] p-8" data-testid={`advantage-${a.num}`}>
                <div className="font-heading font-black text-5xl text-[#F59E0B]/30 mb-4">{a.num}</div>
                <h3 className="font-heading font-bold uppercase text-lg mb-3">{a.title}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contacto" className="py-24 lg:py-32 border-t border-[#2E2E33] bg-[#1F1F25]" data-testid="contact-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-px bg-[#F59E0B]" />
              <span className="overline">Cotice Ahora</span>
            </div>
            <h2 className="font-heading font-black uppercase tracking-tighter text-4xl lg:text-5xl leading-[0.95] mb-8">
              Hablemos sobre su <span className="text-[#F59E0B]">Cumplimiento</span>
            </h2>
            <p className="text-[#A1A1AA] text-base leading-relaxed mb-10">
              Nuestro equipo técnico evaluará sus sistemas de medición y le entregará una propuesta a medida. Sin compromisos.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#2E2E33] flex items-center justify-center flex-shrink-0">
                  <ChevronRight className="w-4 h-4 text-[#F59E0B]" />
                </div>
                <div>
                  <div className="font-heading font-bold uppercase text-sm mb-1">Diagnóstico Inicial</div>
                  <p className="text-[#A1A1AA] text-sm">Evaluamos su nivel actual de cumplimiento contra los ordenamientos vigentes.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#2E2E33] flex items-center justify-center flex-shrink-0">
                  <ChevronRight className="w-4 h-4 text-[#F59E0B]" />
                </div>
                <div>
                  <div className="font-heading font-bold uppercase text-sm mb-1">Propuesta Técnica</div>
                  <p className="text-[#A1A1AA] text-sm">Diseño de la póliza ajustada a su operación y obligaciones específicas.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#2E2E33] flex items-center justify-center flex-shrink-0">
                  <ChevronRight className="w-4 h-4 text-[#F59E0B]" />
                </div>
                <div>
                  <div className="font-heading font-bold uppercase text-sm mb-1">Implementación</div>
                  <p className="text-[#A1A1AA] text-sm">Despliegue, capacitación, calibración y auditorías periódicas.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="border border-[#2E2E33] p-8 lg:p-12 bg-[#1A1A1F]" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-2">Nombre</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-base" data-testid="contact-name" />
                </div>
                <div>
                  <label className="overline block mb-2">Empresa</label>
                  <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input-base" data-testid="contact-company" />
                </div>
                <div>
                  <label className="overline block mb-2">Email</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-base" data-testid="contact-email" />
                </div>
                <div>
                  <label className="overline block mb-2">Teléfono</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-base" data-testid="contact-phone" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-2">Servicio de Interés</label>
                  <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="input-base" data-testid="contact-service">
                    {SERVICES.map((s) => <option key={s.title}>{s.title}</option>)}
                    <option>Póliza de Implementación Integral</option>
                    <option>Otro</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-2">Mensaje</label>
                  <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-base resize-none" data-testid="contact-message" />
                </div>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-[#71717A] text-xs font-mono uppercase tracking-widest">// Respondemos en menos de 24h</p>
                <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50" data-testid="contact-submit">
                  {submitting ? "Enviando..." : "Enviar Solicitud"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Portal */}
      <section className="theme-fixed-dark py-20 border-t border-[#2E2E33] bg-[#1A1A1F]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <div className="overline mb-4">// Portal Clientes</div>
            <h3 className="font-heading font-black uppercase text-2xl lg:text-4xl leading-tight max-w-2xl">
              Descargue sus facturas y documentos en línea, 24/7.
            </h3>
          </div>
          <div className="flex gap-3">
            <button onClick={() => navigate("/login")} className="btn-primary" data-testid="cta-login-btn">
              Acceder al Portal <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => navigate("/register")} className="btn-ghost" data-testid="cta-register-btn">
              Registrarse
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
